import { createClient } from '@supabase/supabase-js';
import { User, WorkOrder, Notification, Comment, Environment, ChecklistItem, TechnicianTargets } from '../types';
import { MOCK_USERS, INITIAL_ORDERS, INITIAL_NOTIFICATIONS } from './mockData';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL || 'https://qhxsrxewfhumlmrwpjdr.supabase.co';
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFoeHNyeGV3Zmh1bWxtcndwamRyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk5ODc1MDEsImV4cCI6MjA4NTU2MzUwMX0.0wgZi9d3BcTlf7oCLdiLKRJAEt8XmML8m6JWCviH9R8';

// Service role key — necessária para auth.admin.createUser() e outras operações admin
// Adicione VITE_SUPABASE_SERVICE_KEY no .env.local (nunca expor no lado do cliente em produção)
const SUPABASE_SERVICE_KEY = (import.meta as any).env?.VITE_SUPABASE_SERVICE_KEY || '';

// Inicialização do cliente Supabase (anon — uso geral)
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Cliente admin com service role (sem persistência de sessão)
const supabaseAdmin = SUPABASE_SERVICE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_SERVICE_KEY, {
      auth: { autoRefreshToken: false, persistSession: false },
    })
  : null;

class SupabaseDatabaseService {
  private isInitialized = false;

  /**
   * Inicializa o banco garantindo que os dados básicos existam.
   * Em produção, isso seria gerenciado por migrations.
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    // Em um ambiente Multi-Tenant real, a inicialização não deve semear dados globais.
    // O setup da empresa é feito via RPC setup_nova_empresa.
    this.isInitialized = true;
  }

  // --- Auth & Identity ---
  async login(email: string, password: string): Promise<{ user: User | null, error: any }> {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError || !authData.user) {
      return { user: null, error: authError || new Error("Credenciais inválidas.") };
    }

    // Tentar buscar por auth_id com retry em caso de erro de schema
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data: dbUser, error: dbError } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', authData.user.id)
          .maybeSingle();

        if (!dbError && dbUser) {
          return { user: dbUser as User, error: null };
        }

        // Fallback: buscar por email
        const { data: emailUser, error: emailError } = await supabase
          .from('users')
          .select('*')
          .eq('email', email)
          .maybeSingle();

        if (!emailError && emailUser) {
          // Atualizar auth_id se necessário
          if (!emailUser.auth_id) {
            await supabase
              .from('users')
              .update({ auth_id: authData.user.id })
              .eq('id', emailUser.id);
          }
          return { user: { ...emailUser, auth_id: authData.user.id } as User, error: null };
        }

        // Se ainda não encontrou, aguardar 500ms e tentar novamente
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (e) {
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
      }
    }

    return { user: null, error: new Error("Usuário público não encontrado.") };
  }

  async logout(): Promise<void> {
    await supabase.auth.signOut();
  }

  async getSessionUser(): Promise<User | null> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    
    const { data, error } = await supabase.from('users').select('*').eq('auth_id', session.user.id).single();
    if (!error && data) return data as User;
    return null;
  }

  async registerInAuth(email: string, password: string): Promise<{userId: string | null, error: any}> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return { userId: null, error: new Error('Sessão não encontrada.') };

      const response = await fetch(
        `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': (import.meta as any).env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { userId: null, error: new Error(result.error || 'Erro ao criar usuário.') };
      }

      return { userId: result.userId, error: null };
    } catch (e) {
      console.error('Erro ao criar usuário via Edge Function:', e);
      return { userId: null, error: e };
    }
  }


  /**
   * Obtém a empresa atual do usuário logado
   */
  async getCurrentCompany(): Promise<any | null> {
    const user = await this.getSessionUser();
    // O banco retorna snake_case (empresa_id), mas o tipo usa camelCase (empresaId)
    const empresaId = (user as any)?.empresa_id || user?.empresaId;
    if (!user || !empresaId) return null;

    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .eq('id', empresaId)
      .single();

    if (error || !data) return null;
    return data;
  }

  /**
   * Versão otimizada que recebe o usuário já carregado,
   * evitando uma chamada duplicada a getSessionUser().
   */
  async getCompanyByUser(user: User): Promise<any | null> {
    const empresaId = (user as any)?.empresa_id || user?.empresaId;
    if (!empresaId) return null;

    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .eq('id', empresaId)
      .single();

    if (error || !data) return null;
    return data;
  }

  /**
   * Registra uma nova empresa (Multi-Tenant)
   * 1. Cria usuário no Auth
   * 2. Chama a RPC setup_nova_empresa para criar a empresa e vincular o Admin
   */
  async registerCompany(
    companyName: string,
    adminName: string,
    email: string,
    password: string,
    city: string,
    teamSize: number,
    plan: string = 'pro',
    billingCycle: string = 'monthly'
  ): Promise<{empresaId: string | null, error: any}> {
    try {
      // 1. Criar usuário no Auth via Edge Function
      const response = await fetch(
        `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': (import.meta as any).env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ email, password, skipAuthCheck: true }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.userId) {
        return { empresaId: null, error: new Error(result.error || 'Erro ao criar usuário no Auth.') };
      }

      const authId = result.userId;

      // 2. Criar empresa e usuário público via RPC
      const { data: empresaId, error: rpcError } = await supabase.rpc('setup_nova_empresa', {
        p_auth_id: authId,
        p_user_email: email,
        p_user_name: adminName,
        p_empresa_nome: companyName,
        p_cidade: city,
        p_numero_tecnicos: teamSize,
        p_plano: plan,
        p_billing_cycle: billingCycle
      });

      if (rpcError) {
        console.error('Erro na RPC setup_nova_empresa:', rpcError);
        return { empresaId: null, error: rpcError };
      }

      return { empresaId, error: null };
    } catch (e) {
      console.error('Erro ao registrar empresa:', e);
      return { empresaId: null, error: e };
    }
  }

  async updateCurrentCompanyPlan(plan: string, billingCycle: string): Promise<void> {
    const company = await this.getCurrentCompany();
    if (!company) return;
    await supabase
      .from('empresas')
      .update({ plano: plan, billing_cycle: billingCycle })
      .eq('id', company.id);
  }


  // --- Users ---
  async getUsers(): Promise<User[]> {
    const { data, error } = await supabase.from('users').select('*');
    if (error) {
      console.error('Erro ao buscar usuários:', error);
      return [];
    }
    return data as User[];
  }

  async saveUsers(users: User[]): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) throw new Error("Empresa não identificada para salvar usuários.");

      const usersToSave = users.map(u => ({
        ...u,
        empresa_id: (u as any).empresa_id || u.empresaId || company.id
      }));

      const { error } = await supabase.from('users').upsert(usersToSave);
      
      if (error) {
        if (error.message?.includes('classification') || error.message?.includes('Could not find the')) {
           console.warn('[CMA Supabase] Fallback users save (missing classification column)');
           const fallbackUsers = usersToSave.map(u => {
              const { classification, ...safeU } = u as any;
              return safeU;
           });
           const { error: fallbackError } = await supabase.from('users').upsert(fallbackUsers);
           if (fallbackError) throw fallbackError;
           console.log('✅ Usuários salvos com sucesso (fallback)');
           return;
        }
        throw error;
      }
      console.log('✅ Usuários salvos com sucesso');
    } catch (error: any) {
      console.error('❌ Erro ao salvar usuários:', error);
      throw error;
    }
  }

  /**
   * Deleta um usuário permanentemente do banco de dados.
   * As referências em ordens de serviço (nomes, emails) serão mantidas,
   * mas o ID do usuário não existirá mais no sistema.
   */
  async updateUserAuthCredentials(userId: string, newEmail?: string, newPassword?: string): Promise<void> {
    const { error } = await supabase.rpc('update_user_auth_credentials', {
      p_user_id: userId,
      p_new_email: newEmail ?? null,
      p_new_password: newPassword ?? null,
    });
    if (error) {
      if ((error as any).code === '23505' || error.message?.includes('duplicate key') || error.message?.includes('users_email_partial_key')) {
        throw new Error('Este e-mail já está em uso por outro usuário.');
      }
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('delete_user_completely', { p_user_id: userId });

      if (error) throw error;
      console.log(`[C.M.A Supabase] Usuário ${userId} excluído com sucesso`);
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      throw error;
    }
  }

  async updateUser(user: User): Promise<void> {
    const company = await this.getCurrentCompany();

    // Remove campos que não são colunas reais no banco
    const {
      id: _id,     // não incluir na payload de update; vai no .eq()
      empresaId,   // alias camelCase — coluna real é empresa_id
      auth_id,     // gerenciado pelo sistema de auth
      ...rest
    } = { ...user } as any;

    const dataToUpdate = {
      ...rest,
      empresa_id: (user as any).empresa_id || empresaId || company?.id,
    };

    const { data, error } = await supabase
      .from('users')
      .update(dataToUpdate)
      .eq('id', user.id)
      .select('id');

    if (error) {
      if ((error as any).code === '23505' || error.message?.includes('duplicate key') || error.message?.includes('users_email_partial_key')) {
        throw new Error('Este e-mail já está em uso por outro usuário.');
      }
      // Fallback: remove colunas que podem não existir no banco
      const { classification, cep, street, addressNumber, complement, neighborhood, city, state, address, email: _e, ...safeU } = dataToUpdate as any;
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('users').update(safeU).eq('id', user.id).select('id');
      if (fallbackError) throw fallbackError;
      if (!fallbackData || fallbackData.length === 0)
        throw new Error('Nenhum registro atualizado. Verifique se você tem permissão para editar este usuário.');
      return;
    }

    if (!data || data.length === 0) {
      throw new Error('Nenhum registro atualizado. Verifique se você tem permissão para editar este usuário.');
    }
  }

  // --- Settings ---
  async getTechnicianTargets(): Promise<TechnicianTargets> {
    const { data, error } = await supabase.from('system_settings').select('value').eq('key', 'technician_targets').maybeSingle();
    if (error || !data) {
      const local = localStorage.getItem('technician_targets');
      if (local) return JSON.parse(local);
      return { JUNIOR: 85000, PLENO: 100000, SENIOR: 120000 };
    }
    return data.value as TechnicianTargets;
  }

  async saveTechnicianTargets(targets: TechnicianTargets): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) throw new Error("Empresa não identificada.");

      const { error } = await supabase.from('system_settings').upsert({ 
        key: 'technician_targets', 
        value: targets,
        empresa_id: company.id 
      });
      
      if (error) {
         console.warn('[CMA Supabase] Fallback to localStorage for targets', error.message);
      }
      localStorage.setItem('technician_targets', JSON.stringify(targets));
    } catch (error) {
      console.error('Erro ao salvar metas:', error);
    }
  }

  // --- Orders ---
  async getOrders(): Promise<WorkOrder[]> {
    try {
      // Busca as ordens
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('createdAt', { ascending: false });

      if (ordersError) throw ordersError;
      if (!ordersData || ordersData.length === 0) return [];

      const orderIds = ordersData.map(o => o.id);

      // Busca TODOS os relacionamentos em paralelo (4 queries no total ao invés de N*3)
      const fetchEvents = async () => {
        try {
          const { data, error } = await supabase
            .from('work_order_events')
            .select('*')
            .in('work_order_id', orderIds);
          if (error) return [];
          return data || [];
        } catch (e) {
          return [];
        }
      };

      const [commentsRes, environmentsRes, checklistRes, rawEvents] = await Promise.all([
        supabase
          .from('comments')
          .select('*')
          .in('orderId', orderIds)
          .order('timestamp', { ascending: true }),
        supabase
          .from('environments')
          .select('*')
          .in('orderId', orderIds),
        supabase
          .from('checklist_items')
          .select('*')
          .in('orderId', orderIds),
        fetchEvents()
      ]);

      const allComments = commentsRes.data || [];
      const allEnvironments = environmentsRes.data || [];
      const allChecklistItems = checklistRes.data || [];
      const allEvents = (rawEvents || []).map((d: any) => ({
        id: d.id,
        workOrderId: d.work_order_id,
        workOrderType: d.work_order_type,
        scope: d.scope,
        environmentId: d.environment_id,
        category: d.category,
        title: d.title,
        description: d.description,
        impactDeadline: d.impact_deadline,
        impactWorkdays: d.impact_workdays,
        impactCost: d.impact_cost,
        impactCostBrl: d.impact_cost_brl,
        visibility: d.visibility,
        status: d.status,
        createdBy: d.created_by,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
        impact_scope: d.impact_scope,
        impacted_environment_id: d.impacted_environment_id
      }));

      // Se há ambientes, busca os checklists dos ambientes (por environmentId)
      let envChecklistItems: any[] = [];
      if (allEnvironments.length > 0) {
        const envIds = allEnvironments.map(e => e.id);
        const { data } = await supabase
          .from('checklist_items')
          .select('*')
          .in('environmentId', envIds);
        envChecklistItems = data || [];
      }

      // Monta as ordens em memória
      const orders: WorkOrder[] = ordersData.map(order => {
        const comments = allComments
          .filter(c => c.orderId === order.id)
          .map(c => ({ ...c, timestamp: new Date(c.timestamp) }));

        const assistanceChecklist = allChecklistItems.filter(
          item => item.orderId === order.id && !item.environmentId
        );

        const environments: Environment[] = allEnvironments
            .filter(env => env.orderId === order.id)
            .map(env => ({
              ...env,
              checklist: envChecklistItems.filter(item => item.environmentId === env.id)
            }));

        const events = allEvents.filter((e: any) => e.workOrderId === order.id);

        return {
          ...order,
          comments,
          environments,
          assistanceChecklist: assistanceChecklist.length > 0 ? assistanceChecklist : undefined,
          events,
          workOrderEvents: events
        } as WorkOrder;
      });

      return orders;
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      return [];
    }
  }

  async getOrdersSummary(): Promise<WorkOrder[]> {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('createdAt', { ascending: false });

      if (ordersError) throw ordersError;
      if (!ordersData || ordersData.length === 0) return [];

      const orderIds = ordersData.map((o: any) => o.id);

      // Busca environments e comments em paralelo
      const fetchEvents = async () => {
        try {
          const { data, error } = await supabase
            .from('work_order_events')
            .select('*')
            .in('work_order_id', orderIds);
          if (error) return [];
          return data || [];
        } catch (e) {
          return [];
        }
      };

      const [environmentsRes, commentsRes, checklistRes, rawEvents] = await Promise.all([
        supabase.from('environments').select('*').in('orderId', orderIds),
        supabase.from('comments').select('*').in('orderId', orderIds).order('timestamp', { ascending: true }),
        supabase.from('checklist_items').select('*').in('orderId', orderIds),
        fetchEvents()
      ]);

      const allEnvironments = environmentsRes.data || [];
      const allComments = commentsRes.data || [];
      const allChecklistItems = checklistRes.data || [];
      const allEvents = (rawEvents || []).map((d: any) => ({
        id: d.id,
        workOrderId: d.work_order_id,
        workOrderType: d.work_order_type,
        scope: d.scope,
        environmentId: d.environment_id,
        category: d.category,
        title: d.title,
        description: d.description,
        impactDeadline: d.impact_deadline,
        impactWorkdays: d.impact_workdays,
        impactCost: d.impact_cost,
        impactCostBrl: d.impact_cost_brl,
        visibility: d.visibility,
        status: d.status,
        createdBy: d.created_by,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
        impact_scope: d.impact_scope,
        impacted_environment_id: d.impacted_environment_id
      }));

      // Busca checklists dos ambientes em paralelo
      let envChecklistItems: any[] = [];
      if (allEnvironments.length > 0) {
        const envIds = allEnvironments.map((e: any) => e.id);
        const { data } = await supabase
          .from('checklist_items')
          .select('*')
          .in('environmentId', envIds);
        envChecklistItems = data || [];
      }

      return ordersData.map((order: any) => {
        const comments = allComments
          .filter((c: any) => c.orderId === order.id)
          .map((c: any) => ({ ...c, timestamp: new Date(c.timestamp) }));
        const assistanceChecklist = allChecklistItems
          .filter((item: any) => item.orderId === order.id && !item.environmentId);
        const environments: Environment[] = allEnvironments
          .filter((env: any) => env.orderId === order.id)
          .map((env: any) => ({
            ...env,
            checklist: envChecklistItems.filter((item: any) => item.environmentId === env.id)
          }));
        
        const events = allEvents.filter((e: any) => e.workOrderId === order.id);

        return {
          ...order,
          comments,
          environments,
          assistanceChecklist: assistanceChecklist.length > 0 ? assistanceChecklist : undefined,
          events,
          workOrderEvents: events
        } as WorkOrder;
      });
    } catch (error) {
      console.error('Erro ao buscar ordens (summary):', error);
      return [];
    }
  }

  async saveOrders(orders: WorkOrder[]): Promise<void> {
    for (const order of orders) {
      await this.saveOrder(order);
    }
  }

  async uploadEnvironmentFile(file: File, orderId: string, envId: string): Promise<string | null> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `environments/${orderId}/${envId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('attachments')
        .upload(fileName, file, { upsert: true });
      if (uploadError) {
        console.error('❌ Erro no upload do PDF:', uploadError);
        return null;
      }
      const { data } = supabase.storage.from('attachments').getPublicUrl(fileName);
      console.log('✅ PDF salvo no Storage:', data.publicUrl);
      return data.publicUrl;
    } catch (e) {
      console.error('❌ Erro inesperado no upload:', e);
      return null;
    }
  }

  async saveOrder(order: WorkOrder, pendingFiles?: Map<string, File>): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) throw new Error("Empresa não identificada.");

      const { assistanceChecklist, environments, comments, ...orderData } = order;
      const fullOrderData = {
        ...orderData,
        empresa_id: (orderData as any).empresa_id || (orderData as any).empresaId || company.id
      };

      const { error: orderError } = await supabase.from('orders').upsert(fullOrderData);
      if (orderError) {
        if (orderError.message.includes("isPostAssembly") || orderError.message.includes("is_post_assembly")) {
          const { isPostAssembly, relatedAssemblyWorkOrderId, relatedAssemblyTechnicianId, relatedAssemblyFinishedAt, relatedEnvironmentId, ...fallbackData } = fullOrderData as any;
          const { error: fallbackError } = await supabase.from('orders').upsert(fallbackData);
          if (fallbackError) throw fallbackError;
        } else {
          throw orderError;
        }
      }

      if (comments && comments.length > 0) {
        await supabase.from('comments').upsert(comments.map(c => ({
          ...c, orderId: order.id,
          timestamp: new Date(c.timestamp).toISOString(),
          empresa_id: company.id
        })));
      }

      if (assistanceChecklist && assistanceChecklist.length > 0) {
        await supabase.from('checklist_items').upsert(assistanceChecklist.map(item => ({
          ...item, orderId: order.id, environmentId: null, empresa_id: company.id
        })));
      }

      if (environments && environments.length > 0) {
        const empresaId = company.id || (order as any).empresa_id;
        console.log('💾 Salvando ambientes:', environments.length, 'empresa_id:', empresaId);

        const envsToSave = environments.map((env: any) => {
          const { checklist, _pendingFile, projectFileUrl, ...envData } = env;
          // Se já for uma URL do supabase storage (começa com http), mantém.
          // Se for base64 (começa com data:), salva nulo inicialmente e depois fazemos upload.
          // Caso contrário (vazio ou null), salva null.
          const keepUrl = (projectFileUrl && typeof projectFileUrl === 'string' && projectFileUrl.startsWith('http')) ? projectFileUrl : null;
          return { ...envData, orderId: order.id, empresa_id: empresaId, projectFileUrl: keepUrl };
        });

        const { error: envError } = await supabase.from('environments').upsert(envsToSave);
        if (envError) {
          console.error('❌ Erro ao salvar ambientes:', envError);
          throw envError;
        }
        console.log('✅ Ambientes salvos:', envsToSave.length);

        // Upload PDFs pendentes para o Supabase Storage (seja via pendingFiles map ou base64 string)
        for (const env of environments) {
          let fileToUpload: File | null = null;

          // Caso 1: veio pelo pendingFiles Map (novo pedido)
          if (pendingFiles && pendingFiles.has(env.id)) {
            fileToUpload = pendingFiles.get(env.id)!;
          }
          // Caso 2: veio como base64 no projectFileUrl (edição de pedido existente)
          else if (env.projectFileUrl && typeof env.projectFileUrl === 'string' && env.projectFileUrl.startsWith('data:')) {
            try {
              const fileExt = env.projectFileName?.split('.').pop() || 'pdf';
              const mimeMatch = env.projectFileUrl.match(/:(.*?);/);
              const mimeType = mimeMatch ? mimeMatch[1] : 'application/pdf';
              
              const arr = env.projectFileUrl.split(',');
              const bstr = atob(arr[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
              }
              fileToUpload = new File([u8arr], env.projectFileName || `projeto.${fileExt}`, { type: mimeType });
            } catch (err) {
              console.error('❌ Erro ao decodificar base64 do projeto:', err);
            }
          }

          if (fileToUpload) {
            console.log('📎 Iniciando upload PDF:', fileToUpload.name, 'env:', env.id);
            const publicUrl = await this.uploadEnvironmentFile(fileToUpload, order.id, env.id);
            if (publicUrl) {
              const { error: updateError } = await supabase
                .from('environments')
                .update({ projectFileUrl: publicUrl })
                .eq('id', env.id);
              if (updateError) {
                console.error('❌ Erro ao atualizar URL do PDF:', updateError);
              } else {
                // Atualiza localmente também no objeto em memória
                env.projectFileUrl = publicUrl;
              }
            }
          }
        }

        // Sincroniza checklists — remove itens deletados e upsert os existentes
        for (const env of environments) {
          const checklist = (env as any).checklist || [];
          
          // Deleta itens removidos
          await this.syncEnvironmentChecklist(env.id, checklist);
          
          // Upsert itens restantes
          if (checklist.length > 0) {
            const itemsToSave = checklist.map((item: any) => {
              const stableId = item.id && item.id.length > 5 ? item.id : `${env.id}-${Math.random().toString(36).substr(2, 9)}`;
              item.id = stableId;
              return {
                ...item,
                id: stableId,
                orderId: null,
                environmentId: env.id,
                empresa_id: empresaId
              };
            });
            const { error: clError } = await supabase.from('checklist_items').upsert(itemsToSave);
            if (clError) console.error('❌ Erro ao salvar checklists dos ambientes:', clError);
          }
        }
      }

      console.log('✅ Ordem salva:', order.id);
    } catch (error) {
      console.error('Erro ao salvar ordem:', error);
      throw error;
    }
  }

  async getExpensesByOrder(orderId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('os_expenses')
        .select('*')
        .eq('order_id', orderId)
        .order('registered_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('Erro ao buscar gastos:', e);
      return [];
    }
  }

  async getAllExpenses(filters?: { period?: string; technicianId?: string; status?: string }): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('os_expenses')
        .select('*, orders(clientName, status, technicianId, technicianName, type, technician:users(name), environments(technicianId, technicianName))')
        .order('registered_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('Erro ao buscar todos os gastos:', e);
      return [];
    }
  }

  async addExpense(expense: {
    order_id: string;
    environment_id?: string;
    item_name: string;
    quantity: number;
    unit_value: number;
    registered_by: string;
  }): Promise<any | null> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) throw new Error('Empresa não identificada.');
      const { data, error } = await supabase
        .from('os_expenses')
        .insert({ ...expense, empresa_id: company.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (e) {
      console.error('Erro ao adicionar gasto:', e);
      return null;
    }
  }

  async updateExpense(id: string, updates: {
    item_name?: string;
    environment_id?: string;
    quantity?: number;
    unit_value?: number;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('os_expenses')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao atualizar gasto:', e);
      throw e;
    }
  }

  async deleteExpense(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('os_expenses')
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao excluir gasto:', e);
      throw e;
    }
  }

  async getMarkupQuotes(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('markup_quotes')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('Erro ao buscar orçamentos:', e);
      return [];
    }
  }

  async saveMarkupQuote(quote: any): Promise<any | null> {
    try {
      const company = await this.getCurrentCompany();
      const user = await this.getSessionUser();
      if (!company) throw new Error('Empresa não identificada.');
      const { data, error } = await supabase
        .from('markup_quotes')
        .insert({ ...quote, empresa_id: company.id, created_by: user?.id, updated_at: new Date().toISOString() })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (e) {
      console.error('Erro ao salvar orçamento:', e);
      return null;
    }
  }

  async updateMarkupQuote(id: string, updates: any): Promise<void> {
    try {
      const { error } = await supabase
        .from('markup_quotes')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao atualizar orçamento:', e);
      throw e;
    }
  }

  async deleteMarkupQuote(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('markup_quotes')
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao excluir orçamento:', e);
      throw e;
    }
  }

  async getFinancialSummary(month?: number, year?: number): Promise<any> {
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select('*, environments(*)')
        .eq('type', 'ASSEMBLY')
        .order('createdAt', { ascending: false });
      if (error) throw error;

      const { data: expenses } = await supabase
        .from('os_expenses')
        .select('*');

      const { data: quotes } = await supabase
        .from('markup_quotes')
        .select('comissao_vendedor_pct, comissao_arquiteto_pct, preco_sugerido')
        .eq('status', 'aprovado');

      return { orders: orders || [], expenses: expenses || [], quotes: quotes || [] };
    } catch (e) {
      console.error('Erro ao buscar dados financeiros:', e);
      return { orders: [], expenses: [], quotes: [] };
    }
  }

  async getEmpresaStatus(): Promise<{ status: string; trialEnd: Date | null; daysLeft: number }> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) return { status: 'unknown', trialEnd: null, daysLeft: 0 };

      const status = company.status || 'trial';
      const trialEnd = company.trial_end ? new Date(company.trial_end) : null;
      const daysLeft = trialEnd
        ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;

      return { status, trialEnd, daysLeft };
    } catch (e) {
      return { status: 'unknown', trialEnd: null, daysLeft: 0 };
    }
  }

  // SUPERADMIN: listar todas as empresas com status
  async getAllEmpresasWithStatus(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('empresas')
        .select('*, users!inner(role, email, name)')
        .eq('users.role', 'ADMIN')
        .order('criado_em', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('Erro ao buscar empresas:', e);
      return [];
    }
  }

  // SUPERADMIN: ativar empresa após pagamento
  async activateEmpresa(empresaId: string, plano: string, paymentNotes?: string): Promise<void> {
    try {
      const user = await this.getSessionUser();
      const { error } = await supabase
        .from('empresas')
        .update({
          status: 'active',
          plano,
          activated_at: new Date().toISOString(),
          activated_by: user?.id,
          payment_notes: paymentNotes || null,
          trial_end: null,
        })
        .eq('id', empresaId);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao ativar empresa:', e);
      throw e;
    }
  }

  // SUPERADMIN: suspender empresa
  async suspendEmpresa(empresaId: string, reason?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('empresas')
        .update({ status: 'suspended', payment_notes: reason || null })
        .eq('id', empresaId);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao suspender empresa:', e);
      throw e;
    }
  }

  // SUPERADMIN: estender trial
  async extendTrial(empresaId: string, extraDays: number): Promise<void> {
    try {
      const { data: empresa } = await supabase
        .from('empresas')
        .select('trial_end')
        .eq('id', empresaId)
        .single();
      
      const currentEnd = empresa?.trial_end ? new Date(empresa.trial_end) : new Date();
      const newEnd = new Date(currentEnd);
      newEnd.setDate(currentEnd.getDate() + extraDays);

      const { error } = await supabase
        .from('empresas')
        .update({ trial_end: newEnd.toISOString(), status: 'trial' })
        .eq('id', empresaId);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao estender trial:', e);
      throw e;
    }
  }

  async deleteEmpresa(empresaId: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada.');

      const response = await fetch(
        `${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/delete-empresa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': (import.meta as any).env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ empresaId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao excluir empresa.');
      }
    } catch (e) {
      console.error('Erro ao excluir empresa:', e);
      throw e;
    }
  }

  /**
   * Deleta uma ordem de serviço permanentemente do banco de dados.
   * Graças ao CASCADE DELETE, todos os dados relacionados serão automaticamente excluídos:
   * - Comentários
   * - Ambientes
   * - Itens de checklist
   */
  async deleteOrder(orderId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;
      console.log(`[C.M.A Supabase] Ordem ${orderId} excluída com sucesso`);
    } catch (error) {
      console.error('Erro ao excluir ordem:', error);
      throw error;
    }
  }

  async deleteChecklistItem(itemId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('checklist_items')
        .delete()
        .eq('id', itemId);
      if (error) throw error;
    } catch (e) {
      console.error('Erro ao deletar item de checklist:', e);
    }
  }

  async syncEnvironmentChecklist(envId: string, currentItems: any[]): Promise<void> {
    try {
      // Busca itens existentes no banco para este ambiente
      const { data: existingItems } = await supabase
        .from('checklist_items')
        .select('id')
        .eq('environmentId', envId);

      const existingIds = new Set((existingItems || []).map((i: any) => i.id));
      const currentIds = new Set(currentItems.map(i => i.id));

      // Deleta itens que existem no banco mas não estão mais na lista local
      const toDelete = [...existingIds].filter(id => !currentIds.has(id));
      if (toDelete.length > 0) {
        const { error } = await supabase
          .from('checklist_items')
          .delete()
          .in('id', toDelete);
        if (error) throw error;
        console.log('🗑️ Itens deletados do checklist:', toDelete);
      }
    } catch (e) {
      console.error('Erro ao sincronizar checklist:', e);
    }
  }

  // --- Notifications ---
  async getNotifications(): Promise<Notification[]> {
    const { data, error } = await supabase.from('notifications').select('*').order('timestamp', { ascending: false });
    if (error) {
      console.error('Erro ao buscar notificações:', error);
      return [];
    }
    return (data || []).map(n => ({
      ...n,
      timestamp: new Date(n.timestamp)
    }));
  }

  async saveNotifications(notifications: Notification[]): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) return;

      const notifsToSave = notifications.map(n => ({
        ...n,
        timestamp: (n.timestamp as any) instanceof Date ? (n.timestamp as any).toISOString() : n.timestamp,
        empresa_id: (n as any).empresa_id || (n as any).empresaId || company.id
      }));

      await supabase.from('notifications').upsert(notifsToSave);
    } catch (error) {
      console.error('Erro ao salvar notificações:', error);
    }
  }

  /**
   * Insere UMA notificação nova no banco.
   * Faz fallback sem os campos alertType/navigateTo se as colunas ainda não existirem.
   */
  async insertNotification(notif: Notification): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) return;

      const fullNotif = {
        ...notif,
        empresa_id: (notif as any).empresa_id || (notif as any).empresaId || company.id
      };

      const { error } = await supabase.from('notifications').insert(fullNotif);
      if (error) {
        if (error.message?.includes('alertType') || error.message?.includes('navigateTo')) {
          const { alertType, navigateTo, ...safeNotif } = fullNotif as any;
          const { error: fallbackError } = await supabase.from('notifications').insert(safeNotif);
          if (fallbackError) throw fallbackError;
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Erro ao inserir notificação:', error);
    }
  }

  /**
   * Marca apenas uma notificação como lida (UPDATE cirúrgico, não upsert em massa).
   */
  async markNotificationRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id);
    if (error) console.error('Erro ao marcar notificação como lida:', error);
  }

  /**
   * Marca todas as notificações de um usuário como lidas.
   */
  async markAllNotificationsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('userId', userId)
      .eq('read', false);
    if (error) console.error('Erro ao marcar todas as notificações como lidas:', error);
  }

  /**
   * Deleta uma notificação permanentemente do banco de dados.
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
      console.log(`[C.M.A Supabase] Notificação ${notificationId} excluída com sucesso`);
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      throw error;
    }
  }

  /** Deleta todas as notificações lidas de um usuário de uma só vez. */
  async deleteReadNotifications(userId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('userId', userId)
      .eq('read', true);
    if (error) console.error('Erro ao excluir notificações lidas:', error);
  }

  /** Deleta uma lista específica de notificações por IDs. */
  async deleteNotifications(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('notifications')
      .delete()
      .in('id', ids);
    if (error) console.error('Erro ao excluir notificações selecionadas:', error);
  }

  // --- Audit Logs (LGPD) ---
  async logAudit(userId: string, action: string, orderId?: string, details?: any): Promise<void> {
    try {
      const company = await this.getCurrentCompany();
      if (!company) return;

      await supabase.from('audit_logs').insert({
        userId,
        action,
        orderId,
        details,
        empresa_id: company.id
      });
    } catch (e) {
      console.error('Erro ao salvar audit log:', e);
    }
  }
  
  // --- Occurrences / Events ---
  async getWorkOrderEvents(workOrderId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('work_order_events')
        .select(`
          *,
          attachments:work_order_event_attachments(*)
        `)
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
          console.warn('[CMA Supabase] migration for work_order_events not yet applied via SQL.');
          return [];
        }
        console.error("Supabase Select events error:", error);
        throw error;
      }
      
      // Fetch resolving details manually to bypass FK limitations
      const authIds = Array.from(new Set(data?.map(d => d.created_by).filter(Boolean)));
      const envIds = Array.from(new Set(data?.map(d => d.environment_id).filter(Boolean)));
      
      let mappedUsers: any[] = [];
      if (authIds.length > 0) {
        const { data: u } = await supabase.from('users').select('auth_id, name').in('auth_id', authIds);
        if (u) mappedUsers = u;
      }
      
      let mappedEnvs: any[] = [];
      if (envIds.length > 0) {
        const { data: e } = await supabase.from('environments').select('id, name').in('id', envIds);
        if (e) mappedEnvs = e;
      }
      
      // format response
      return data?.map(d => ({
        id: d.id,
        workOrderId: d.work_order_id,
        workOrderType: d.work_order_type,
        scope: d.scope,
        environmentId: d.environment_id,
        category: d.category,
        title: d.title,
        description: d.description,
        impactDeadline: d.impact_deadline,
        impactWorkdays: d.impact_workdays,
        impactCost: d.impact_cost,
        impactCostBrl: d.impact_cost_brl,
        visibility: d.visibility,
        status: d.status,
        createdBy: d.created_by,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
        impact_scope: d.impact_scope,
        impacted_environment_id: d.impacted_environment_id,
        createdByName: mappedUsers.find(u => u.auth_id === d.created_by)?.name || 'Desconhecido(a)',
        environmentName: mappedEnvs.find(e => e.id === d.environment_id)?.name,
        attachments: d.attachments?.map((att: any) => ({
           id: att.id,
           eventId: att.event_id,
           workOrderId: att.work_order_id,
           fileUrl: att.file_url,
           fileName: att.file_name,
           fileType: att.file_type,
           createdAt: att.created_at
        })) || []
      })) || [];
    } catch (e) {
      console.error('Erro ao buscar eventos da OS:', e);
      return [];
    }
  }

  async createWorkOrderEvent(eventParams: any, attachments?: File[]): Promise<any> {
    try {
      const user = await this.getSessionUser();
      const company = await this.getCurrentCompany();
      if (!company) throw new Error("Empresa não identificada.");
      
      // Snake case payload expected by Supabase
      const payload = {
        work_order_id: eventParams.workOrderId,
        work_order_type: eventParams.workOrderType,
        scope: eventParams.scope,
        environment_id: eventParams.environmentId || null,
        category: eventParams.category,
        title: eventParams.title,
        description: eventParams.description,
        impact_deadline: eventParams.impactDeadline,
        impact_workdays: eventParams.impactWorkdays || null,
        impact_cost: eventParams.impactCost,
        impact_cost_brl: eventParams.impactCostBrl || null,
        visibility: eventParams.visibility || 'internal',
        status: eventParams.status || 'open',
        created_by: user?.auth_id,
        empresa_id: company.id,
        impact_scope: eventParams.impact_scope || (eventParams.scope === 'work_order' ? 'general' : 'environment'),
        impacted_environment_id: eventParams.impacted_environment_id || (eventParams.scope === 'environment' ? eventParams.environmentId : null)
      };

      const { data: newEvent, error } = await supabase
        .from('work_order_events')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      
      if (attachments && attachments.length > 0) {
          for (let file of attachments) {
              const fileExt = file.name.split('.').pop();
              const fileName = `${newEvent.id}/${Math.random().toString(36).substring(7)}.${fileExt}`;
              
              const { error: uploadError } = await supabase.storage
                .from('attachments')
                .upload(fileName, file);
                
              if (uploadError) {
                  console.error('Upload Error:', uploadError);
                  continue;
              }
              
              const { data: publicUrlData } = supabase.storage
                .from('attachments')
                .getPublicUrl(fileName); // Fallback depending on bucket config
              
              const { error: attachmentError } = await supabase.from('work_order_event_attachments').insert({
                 event_id: newEvent.id,
                 work_order_id: eventParams.workOrderId,
                 file_url: publicUrlData.publicUrl,
                 file_name: file.name,
                 file_type: file.type,
                 empresa_id: company.id
              });
              
              if (attachmentError) {
                  console.error('Erro ao salvar anexo da ocorrência:', attachmentError);
              }
          }
      }

      const envInfo = payload.environment_id ? ` para o ambiente` : '';
      await this.logAudit(user?.id || '', `Ocorrência registrada: ${payload.title}${envInfo}`, payload.work_order_id);

      // --- Notifications ---
      const orderRes = await supabase.from('orders').select('"technicianId"').eq('id', payload.work_order_id).single();
      const techId = orderRes.data?.technicianId;
      const { data: targetUsers } = await supabase.from('users').select('id, role').in('role', ['ADMIN', 'ASSISTANT']);
      const targetsToNotify = new Set((targetUsers || []).map(u => u.id));
      if (techId) targetsToNotify.add(techId);
      targetsToNotify.delete(user?.id);

      for (let tId of Array.from(targetsToNotify)) {
         if(!tId) continue;
         await this.insertNotification({
            id: Math.random().toString(36).substring(7),
            userId: tId as string,
            title: 'Nova Ocorrência',
            message: `A ocorrência '${payload.title}' foi registrada na ordem de serviço.`,
            type: 'WARNING',
            read: false,
            timestamp: new Date().toISOString(),
            relatedOrderId: payload.work_order_id,
            navigateTo: 'DETAILS'
         });
      }

      return newEvent;
    } catch (e) {
      console.error('Erro ao criar ocorrência:', e);
      throw e;
    }
  }

  async updateWorkOrderEvent(eventId: string, payload: any, attachments?: File[]): Promise<void> {
    try {
      const user = await this.getSessionUser();
      const company = await this.getCurrentCompany();
      if (!company) throw new Error("Empresa não identificada.");

      const { error } = await supabase
        .from('work_order_events')
        .update({
           category: payload.category,
           title: payload.title,
           description: payload.description,
           impact_deadline: payload.impactDeadline,
           impact_workdays: payload.impactDeadline ? payload.impactWorkdays : null,
           impact_cost: payload.impactCost,
           impact_cost_brl: payload.impactCost ? payload.impactCostBrl : null,
           visibility: payload.visibility,
           updated_at: new Date().toISOString(),
           impact_scope: payload.impact_scope || (payload.scope === 'work_order' ? 'general' : 'environment'),
           impacted_environment_id: payload.impacted_environment_id || (payload.scope === 'environment' ? payload.environmentId : null)
        })
        .eq('id', eventId);

      if (error) throw error;
      
      if (attachments && attachments.length > 0) {
          for (let file of attachments) {
              const fileExt = file.name.split('.').pop();
              const fileName = `${eventId}/${Math.random().toString(36).substring(7)}.${fileExt}`;
              
              const { error: uploadError } = await supabase.storage
                .from('attachments')
                .upload(fileName, file);
                
              if (uploadError) {
                  console.error('Upload Error:', uploadError);
                  continue;
              }
              
              const { data: publicUrlData } = supabase.storage
                .from('attachments')
                .getPublicUrl(fileName);
              
              const { error: attachmentError } = await supabase.from('work_order_event_attachments').insert({
                 event_id: eventId,
                 work_order_id: payload.workOrderId,
                 file_url: publicUrlData.publicUrl,
                 file_name: file.name,
                 file_type: file.type,
                 empresa_id: company.id
              });
              
              if (attachmentError) {
                  console.error('Erro ao salvar anexo na edição da ocorrência:', attachmentError);
              }
          }
      }
      
      await this.logAudit(user?.id || '', `Ocorrência Editada: ${payload.title}`, payload.workOrderId);
      
      // Notify update
      const orderRes = await supabase.from('orders').select('"technicianId"').eq('id', payload.workOrderId).single();
      const techId = orderRes.data?.technicianId;
      const { data: targetUsers } = await supabase.from('users').select('id, role').in('role', ['ADMIN', 'ASSISTANT']);
      const targetsToNotify = new Set((targetUsers || []).map(u => u.id));
      if (techId) targetsToNotify.add(techId);
      targetsToNotify.delete(user?.id);

      for (let tId of Array.from(targetsToNotify)) {
         if(!tId) continue;
         await this.insertNotification({
            id: Math.random().toString(36).substring(7),
            userId: tId as string,
            title: 'Ocorrência Atualizada',
            message: `A ocorrência '${payload.title}' foi alterada.`,
            type: 'INFO',
            read: false,
            timestamp: new Date().toISOString(),
            relatedOrderId: payload.workOrderId,
            navigateTo: 'DETAILS'
         });
      }
    } catch(e) {
      console.error('Erro atualizar ocorrência:', e);
      throw e;
    }
  }

  async deleteWorkOrderEvent(eventId: string, workOrderId: string, title?: string): Promise<void> {
     try {
       const user = await this.getSessionUser();
       const { error } = await supabase.from('work_order_events').delete().eq('id', eventId);
       if (error) throw error;
       await this.logAudit(user?.id || '', `Ocorrência Excluída: ${title || eventId}`, workOrderId);
     } catch(e) {
       console.error('Erro ao excluir ocorrência', e);
       throw e;
     }
  }
  
  async updateWorkOrderEventStatus(eventId: string, status: 'open' | 'resolved'): Promise<void> {
    try {
        const { error } = await supabase.from('work_order_events').update({ status, updated_at: new Date().toISOString() }).eq('id', eventId);
        if (error) throw error;
    } catch(e) {
        console.error('Erro atualizar status occ.', e);
        throw e;
    }
  }
}

export const db = new SupabaseDatabaseService();
